import {
  CustomCollapseCard,
  CustomImage,
  CustomTable,
  errorDialog,
  Text,
} from '@src/components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '@src/styles/myInvoices/invoiceList.scss';
import save from '@src/assets/images/invoices/save.png';
import downloadIcon from '@src/assets/icons/download.svg';
import invoicePdf from '@src/assets/images/invoices/invoice.png';
import icnPDF from '@src/assets/icons/icn-pdf.svg';
import icnExcel from '@src/assets/icons/icn-excel3.png';
import excel from '@src/assets/images/invoices/excel.png';
import { useDispatch, useSelector } from 'react-redux';
import { getPagedList } from '@src/store/slice/invoiceSlice';
import reportsServices from '@src/services/reports.services';
import invoicesServices from '@src/services/invoices.services';
import download from 'js-file-download';
import { DownCircleOutlined, UpCircleOutlined } from '@ant-design/icons';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { handleAuthStatus } from '@src/utils/utils';
import PropTypes from 'prop-types';
import { warningDialog } from '@src/components/CustomDialog';
import { Button, Radio } from 'antd';
import { VouIdCode } from '@src/store/enums';

function getExpandIcon() {
  return ({ expanded, onExpand, record }) =>
    expanded ? (
      <UpCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    ) : (
      <DownCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    );
}

function getExpandedRowRender(
  paymentInfo,
  userAuthorizationStatus,
  invoiceArchiveFileDownload,
  pdfDownload,
  excelDownload,
) {
  return (record) => {
    return (
      <div className="custom-expanded-row" key={record.id}>
        <div className="colum-title">
          <span>
            <Text t="invoiceDate" />:
          </span>{' '}
          <span>{record.invoiceDate}</span>
        </div>
        <div className="colum-title">
          <span>
            <Text t="expiryDate" />:
          </span>{' '}
          <span>{record.invoiceLimitDate}</span>
        </div>
        <div className="colum-title">
          <span>
            <Text t="excludesVat" />:
          </span>{' '}
          <span>{record.withoutKDVAmount}₺</span>
        </div>
        <div className="colum-title">
          <span>
            <Text t="invoicePrice" />:
          </span>{' '}
          <span>{record.totalInvoiceAmount}₺</span>
        </div>
        <div className="colum-title">
          <span>
            <Text t="paymentInfo" />:
          </span>{' '}
          <span> {paymentInfo(record.icnPaymentStatu)} </span>
        </div>
        <div className="colum-title">
          <span>
            <Text t="unpaidAmount" />:
          </span>{' '}
          <span>{record.balanceDue}₺</span>
        </div>
        {!handleAuthStatus(userAuthorizationStatus, 'MyInvoicesInvoiceExport') && (
          <InvoiceDownloadActions
            className="mobile-action-btn"
            record={record}
            invoiceArchiveFileDownload={invoiceArchiveFileDownload}
            pdfDownload={pdfDownload}
            excelDownload={excelDownload}
          />
        )}
      </div>
    );
  };
}

const InvoiceDownloadActions = ({
  record,
  invoiceArchiveFileDownload,
  pdfDownload,
  excelDownload,
  className = 'invoice-actions',
}) => (
  <div className={className}>
    {record.fileExists === 1 && (
      <CustomImage
        className="invoice-pdf"
        src={invoicePdf}
        onClick={() => invoiceArchiveFileDownload(record)}
      />
    )}

    <Button
      className="download-button"
      onClick={() => pdfDownload(record)}
      icon={<CustomImage src={downloadIcon} />}
    >
      <Text t="saveBtn" />
    </Button>

    <CustomImage
      className="invoice-excel"
      src={excel}
      onClick={() => excelDownload(record)}
    />
  </div>
);

InvoiceDownloadActions.propTypes = {
  record: PropTypes.object.isRequired,
  invoiceArchiveFileDownload: PropTypes.func.isRequired,
  pdfDownload: PropTypes.func.isRequired,
  excelDownload: PropTypes.func.isRequired,
  className: PropTypes.string,
};

const InvoiceList = ({ isCompliments = false, isMarket = false, vouId }) => {
  const dispatch = useDispatch();
  const { invoicesList } = useSelector((state) => state.invoice);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const [expandedRowsInvocies, setExpandedRowsInvocies] = useState([]);
  const isMobil = useMediaQuery({ query: '(max-width: 767.98px)' });
  const { userAuthorizationStatus, userAuthorizationGiftStatus, userAuthorizationMarketStatus } =
    useSelector((state) => state?.user);
  const [downloadExcel, setDownloadExcel] = useState(false);
  const [ready, setReady] = useState(null);

  const userAuthorizationStatusForType = useMemo(() => {
    if (isMarket) return userAuthorizationMarketStatus;
    if (isCompliments) return userAuthorizationGiftStatus;
    return userAuthorizationStatus;
  }, [isMarket, isCompliments]);

  useEffect(() => {
    loadInvoiceList();
  }, [dispatch]);

  useEffect(() => {
    if (ready) {
      if (downloadExcel) excelDownload(ready);
      else pdfDownload(ready);
      setDownloadExcel(false);
      setReady(null);
    }
  }, [ready]);

  const loadInvoiceList = useCallback(() => {
    const body = {
      customerId: selectedCustomer?.customerId,
      vouId: vouId,
    };

    dispatch(getPagedList({ body }));
  }, [dispatch, selectedCustomer?.customerId, vouId]);

  const excelDownload = useCallback(
    async (record) => {
      const data = {
        customerId: selectedCustomer?.customerId,
        downloadType: 2,
        orderId: record?.orderNumber,
        vouId: record?.invoiceVouCode,
        invoiceId: record?.invoiceId,
      };
      const invoiceDate = record?.invoiceDate.split('T')[0].replaceAll('_', '/');

      try {
        const blob = await invoicesServices.downloadInvoiceFile(data);
        download(blob, `${record?.orderNumber} - ${invoiceDate}.xlsx`);
      } catch (error) {
        errorDialog({
          title: <Text t="error" />,
          message: error?.statusText,
        });
      }
    },
    [selectedCustomer],
  );

  const pdfDownload = useCallback(
    async (record) => {
      let data = {
        customerId: selectedCustomer?.customerId,
        orderId: record?.orderNumber,
        vouId: record?.invoiceVouCode,
        invoiceId: record?.invoiceId,
      };

      try {
        const blob = await reportsServices.getOrderSummaryPdfReport(data);
        download(blob, `${record?.orderNumber} - ${record?.invoiceDate.split('T')[0]}.pdf`);
      } catch (error) {
        errorDialog({
          title: <Text t="error" />,
          message: error?.statusText,
        });
      } finally {
        setReady(null);
      }
    },
    [selectedCustomer],
  );

  const invoiceArchiveFileDownload = useCallback(
    async (record) => {
      let data = {
        customerId: selectedCustomer?.customerId,
        invoiceId: record?.invoiceId,
        vouId: vouId,
      };

      try {
        const blob = await invoicesServices.downloadInvoiceArchiveFile(data);
        download(blob, `${record?.orderNumber} - ${record?.invoiceDate.split('T')[0]}.pdf`);
      } catch (error) {
        errorDialog({
          title: <Text t="error" />,
          message: error?.statusText,
        });
      }
    },
    [selectedCustomer],
  );

  const paymentTextMap = {
    0: 'notPaid',
    1: 'partiallyPaid',
    2: 'paid',
  };

  const paymentInfo = (status) => {
    const translationKey = paymentTextMap[status];

    return translationKey ? <Text t={translationKey} /> : null;
  };

  const columns = [
    {
      title: <Text t="invoiceSerialNo" />,
      dataIndex: 'invoiceSerialNumber',
      key: 'invoiceSerialNumber',
      align: 'left',
      width: 240,
      sorter: (a, b) => a.invoiceSerialNumber.localeCompare(b.invoiceSerialNumber),
    },
    {
      title: <Text t="orderNoUpperCase" />,
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 40,
      align: 'center',
    },
    {
      title: <Text t="invoiceDate" />,
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 47,
      align: 'center',
      responsive: ['md'],
    },
    {
      title: <Text t="expiryDate" />,
      dataIndex: 'invoiceLimitDate',
      key: 'invoiceLimitDate',
      width: 40,
      align: 'center',
      responsive: ['md'],
    },
    {
      title: <Text t="excludesVat" />,
      dataIndex: 'withoutKDVAmount',
      key: 'withoutKDVAmount',
      width: 50,
      align: isCompliments || isMarket ? 'center' : 'right',
      responsive: ['md'],
    },
    {
      title: <Text t="invoicePrice" />,
      dataIndex: 'totalInvoiceAmount',
      key: 'totalInvoiceAmount',
      width: 47,
      align: isCompliments || isMarket ? 'center' : 'right',
      responsive: ['md'],
    },
    {
      title: <Text t="paymentInfo" />,
      dataIndex: 'icnPaymentStatu',
      key: 'icnPaymentStatu',
      width: 44,
      align: 'center',
      responsive: ['md'],
      render: (text) => {
        return (
          <span
            style={
              text === 0
                ? {
                    backgroundColor: '#ffeeed',
                    borderRadius: 32,
                    paddingLeft: 12,
                    paddingRight: 12,
                    paddingBottom: 8,
                    paddingTop: 8,
                    color: '#bd0d00',
                    width: 86,
                    display: 'flex',
                    justifyContent: 'center',
                  }
                : {
                    backgroundColor: '#d1fadf',
                    borderRadius: 32,
                    paddingLeft: 12,
                    paddingRight: 12,
                    paddingBottom: 8,
                    paddingTop: 8,
                    color: '#027a48',
                    width: 86,
                    display: 'flex',
                    justifyContent: 'center',
                  }
            }
          >
            {paymentInfo(text)}
          </span>
        );
      },
    },
    {
      title: <Text t="unpaidAmount" />,
      dataIndex: 'balanceDue',
      key: 'balanceDue',
      width: 75,
      align: isCompliments || isMarket ? 'center' : 'right',
      responsive: ['md'],
    },
  ];
  !handleAuthStatus(userAuthorizationStatusForType, 'MyInvoicesInvoiceExport') &&
    columns.push({
      title: '',
      dataIndex: 'index',
      key: 'index',
      width: isCompliments || isMarket ? 90 : 100,
      align: 'center',
      responsive: ['md'],
      render: (_, record) => {
        if (isCompliments || isMarket) {
          return (
            <div className="invoice-actions">
              <button
                onClick={() => {
                  warningDialog({
                    title: 'İndir',
                    width: 270,
                    notWarnIcon: true,
                    htmlContent: (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Radio.Group
                          id="admin-managementUser-addUser-custom-radio-input-content"
                          onChange={(e) => {
                            setDownloadExcel(!e?.target?.value);
                          }}
                          defaultValue={true}
                          className="custom-radio-input-content"
                        >
                          <Radio
                            id="admin-managementUser-addUser-all-delivery-address-radio"
                            value={true}
                          >
                            <div className="comp-modal-text">
                              <Text t="downloadAsPdf" />
                              <CustomImage
                                style={{ marginLeft: 10, width: 24, height: 24 }}
                                src={icnPDF}
                              />
                            </div>
                          </Radio>
                          <Radio
                            id="admin-managementUser-addUser-just-choose-delivery-address-radio"
                            value={false}
                          >
                            <div className="comp-modal-text">
                              <Text t="downloadAsExcel" />
                              <CustomImage
                                style={{ marginLeft: 10, width: 24, height: 24 }}
                                src={icnExcel}
                              />
                            </div>
                          </Radio>
                        </Radio.Group>

                        <button
                          style={{ marginTop: 20 }}
                          className="utts-ok-button comp-warn-confirm"
                          onClick={async () => {
                            clickDownload(record);
                          }}
                        >
                          <Text t="download2" />
                        </button>
                        <button
                          className="utts-cancel-button gift-cancel-order-cancel"
                          onClick={null}
                        >
                          <Text t="giveUp" />
                        </button>
                      </div>
                    ),
                    className: 'warningModal-utts not-show-warning-buttons',
                    okButtonProps: 'not-show-warning-buttons',
                    onOk: async () => {},
                    onCancel: async () => {},
                  });
                }}
                style={{
                  backgroundColor: '#334155',
                  width: 120,
                  height: 48,
                  borderRadius: 12,
                  borderWidth: 0,
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: 'flex',
                  color: '#fff',
                }}
              >
                <Text t="downloadOrderSummary" />
              </button>
              {record.fileExists === 1 ? (
                <button
                  onClick={() => invoiceArchiveFileDownload(record)}
                  style={{
                    backgroundColor: '#f66301',
                    width: 80,
                    height: 48,
                    borderRadius: 12,
                    borderWidth: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                    color: '#fff',
                  }}
                >
                  <Text t="downloadInvoice" />
                </button>
              ) : null}
            </div>
          );
        }
        return (
          <InvoiceDownloadActions
            record={record}
            invoiceArchiveFileDownload={invoiceArchiveFileDownload}
            pdfDownload={pdfDownload}
            excelDownload={excelDownload}
          />
        );
      },
    });
  const clickDownload = (record) => {
    setReady(record);
  };

  return (
    <CustomCollapseCard className="invoice-list-card" cardTitle={<Text t="invoices" />}>
      <CustomTable
        pagination={true}
        isScroll={!isMobil}
        dataSource={invoicesList}
        columns={columns}
        className="invoice-list-table"
        rowKey={(record) => `invoice-list-${record?.id}`}
        expandedRowKeys={expandedRowsInvocies}
        expandable={{
          showExpandColumn: false,
          expandIconColumnIndex: 37,
          onExpand: (expanded, record, key) =>
            expandedRowsInvocies === key
              ? setExpandedRowsInvocies(null)
              : setExpandedRowsInvocies(key),
          expandIcon: getExpandIcon(),
          expandedRowRender: getExpandedRowRender(
            paymentInfo,
            userAuthorizationStatusForType,
            invoiceArchiveFileDownload,
            pdfDownload,
            excelDownload,
          ),
        }}
      />
    </CustomCollapseCard>
  );
};
InvoiceList.propTypes = {
  isCompliments: PropTypes.bool,
  isMarket: PropTypes.bool,
  vouId: PropTypes.number,
};

export default InvoiceList;
